// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { JobFormModal, JobKanbanItem } from '~/components/module/job';
import {
  BaseDatePicker,
  BaseFilters,
  BaseKanban,
  BaseSelect,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
// Others
import { EMPTY_STRING, FILTER_DEFAULT_VALUES, optionsTypePage } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IChangeStatusJob, IParamQueryJobKanban, ItemTypeJob } from '~/utils/interface/jobs';
import {
  ColumnTypeKanban,
  IBaseOption,
  IBodyUpdate,
  IResultCallApiFn,
  IStyleColumnKanban,
  ItemChangeKanban,
} from '~/utils/interface/common';
import { jobActions } from '~/thunks/job/jobSlice';
import { formatTitleCase } from '~/utils/helper/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { JOB_PERMISSIONS } from '~/utils/constants/permissions';
import { changeStatusJob, deleteJob, getListJobKanban } from '~/thunks/job/jobThunk';
// Styles, images, icons
import styles from './JobKanban.module.scss';
import { icons } from '~/assets';

type Props = {
  isTypePage?: string;
  onChangeType?: (value: string) => void;
};

const cx = classNames.bind(styles);

const JobKanban = (props: Props) => {
  //#region Destructuring Props
  const { isTypePage, onChangeType } = props;

  const { canCreateJobs, canViewJobs, canViewAllJobs, canDeleteJobs } = usePermissions(JOB_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshJobList } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramObject, setParamObject] = useState<IParamQueryJobKanban>({});
  const [filterParams, setFilterParams] = useState<IParamQueryJobKanban>(FILTER_DEFAULT_VALUES);
  const [listJobKanban, setListJobKanban] = useState<ColumnTypeKanban<ItemTypeJob>[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [idJob, setIdJob] = useState<string>(EMPTY_STRING);
  const [isOpenJobFormModal, setIsOpenJobFormModal] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListJob(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshJobList) return;

    handleGetListJob(paramObject);

    dispatch(jobActions.setRefreshJobList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshJobList]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleGetListJob = (params: IParamQueryJobKanban) => {
    setIsLoading(true);

    dispatch(getListJobKanban(params))
      .unwrap()
      .then((res) => {
        const { responses, totalItems } = res?.data;

        if (!responses) return;

        const jobPosition = [
          'finalized',
          'scheduled',
          'loaded',
          'delivered',
          'delivery incomplete',
          'picked up',
          'returned',
          'picked up incomplete',
          'invoiced',
          'complete',
        ];

        const sortedData = responses.sort((a, b) => jobPosition.indexOf(a.status) - jobPosition.indexOf(b.status));

        const convertData: ColumnTypeKanban<ItemTypeJob>[] = sortedData.map((item, index) => ({
          id: String(index + 1),
          uuid: index + 1,
          name: formatTitleCase(item.status),
          items: item.jobs,
        }));

        setListJobKanban(convertData);
        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchOptionsTypeList = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTypePage,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleAddJob = () => {
    handleShowJobFormModal();
  };

  const handleShowJobFormModal = () => {
    setIsOpenJobFormModal((prev) => !prev);
  };

  const handleItemChange = (_data: ItemChangeKanban<ItemTypeJob>, item?: ItemTypeJob, newStatus?: string) => {
    setIsLoading(true);

    const body: IBodyUpdate<IChangeStatusJob> = {
      id: item?.id || EMPTY_STRING,
      body: {
        status: newStatus?.toLowerCase() || EMPTY_STRING,
      },
    };

    dispatch(changeStatusJob(body))
      .unwrap()
      .then(() => {
        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRemoveKanbanItem = (item: ItemTypeJob) => {
    if (!item?.id) return;

    setIdJob(item?.id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdJob(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteJob(idJob))
      .unwrap()
      .then(() => {
        setIdJob(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeDate = (date: string | { startDate: string; endDate: string }) => {
    if (typeof date !== 'string') {
      const { startDate, endDate } = date;
      setFilterParams((prev) => ({
        ...prev,
        startDate,
        endDate,
      }));
    }
  };

  const handleApplyFilter = (values: IParamQueryJobKanban | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        startDate: values?.startDate,
        endDate: values?.endDate,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      startDate: undefined,
      endDate: undefined,
    });
  };

  const handleGetColorNameColumn = (id: string): IStyleColumnKanban => {
    switch (id) {
      case '1':
        return {
          backgroundColor: '#FFF7ED',
          color: '#C2410C',
          borderColor: '#FED7AA',
        };
      case '2':
        return {
          backgroundColor: '#F0FDF4',
          borderColor: '#BBF7D0',
          color: '#15803D',
        };
      case '3':
        return {
          backgroundColor: '#FFF7ED',
          color: '#C2410C',
          borderColor: '#FED7AA',
        };
      case '4':
        return {
          backgroundColor: '#F0FDF4',
          borderColor: '#BBF7D0',
          color: '#15803D',
        };
      case '5':
        return {
          backgroundColor: '#F0F9FF',
          color: '#0369A1',
          borderColor: '#BAE6FD',
        };
      case '6':
        return {
          backgroundColor: '#F0FDF4',
          borderColor: '#BBF7D0',
          color: '#15803D',
        };
      case '7':
        return {
          backgroundColor: '#FFF7ED',
          color: '#C2410C',
          borderColor: '#FED7AA',
        };
      case '8':
        return {
          backgroundColor: '#F0F9FF',
          color: '#0369A1',
          borderColor: '#BAE6FD',
        };
      case '9':
        return {
          backgroundColor: '#F0FDF4',
          borderColor: '#BBF7D0',
          color: '#15803D',
        };
      case '10':
        return {
          backgroundColor: '#FFF7ED',
          color: '#C2410C',
          borderColor: '#FED7AA',
        };
      default:
        return {
          backgroundColor: '#E6F4FF',
          color: '#0369A1',
          borderColor: '#BAE6FD',
        };
    }
  };
  //#endregion Handle Function

  return (
    <Box id='jobKanbanComponent' className={cx('container')}>
      <BaseToolbar title={t('job_title_jobs_board')} total={t('job_title_total_jobs', { total: totalItems })}>
        {canCreateJobs && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='outlined'
              onClick={handleAddJob}
              startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
            >
              {t('job_title_add_job')}
            </Button>
          </Box>
        )}

        <BaseFilters<IParamQueryJobKanban>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseDatePicker
                mode='rangeDate'
                label={t('job_title_filter_date')}
                value={{
                  startDate: filterParams?.startDate || EMPTY_STRING,
                  endDate: filterParams?.endDate || EMPTY_STRING,
                }}
                onChange={handleChangeDate}
              />
            </Stack>
          )}
        </BaseFilters>

        <Box>
          <BaseSelect
            gap={4}
            height={39}
            styleType='outlined'
            placeholder={t('job_title_select')}
            callApiFn={fetchOptionsTypeList}
            value={isTypePage}
            onChange={(item) => onChangeType?.(item?.value || EMPTY_STRING)}
          />
        </Box>
      </BaseToolbar>

      <BaseKanban
        data={canViewAllJobs || canViewJobs ? listJobKanban : []}
        styleColumn={handleGetColorNameColumn}
        onItemChange={handleItemChange}
        renderItem={(item: ItemTypeJob) => (
          <JobKanbanItem item={item} onRemove={handleRemoveKanbanItem} isDelete={canDeleteJobs} />
        )}
      />

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isOpenJobFormModal && <JobFormModal onClose={handleShowJobFormModal} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default JobKanban;
